import { useCallback, useMemo, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { TailSpin } from 'react-loader-spinner';
import { DeviceIds, deviceType } from '@/core/environment/deviceType';
import useCallbackReference from '@/core/stateManager/useCallbackReference';
import { useTracker } from '@/core/tracking';
import DownloadSidebar from '@/features/downloadMedia/downloadSidebar/DownloadSidebar';
import AIModelMismatchModal from '@/features/downloadMedia/modals/AIModelMismatchModal';
import {
  usePaymentService,
  useUploaderService,
} from '@/layout/appWrapper/ServiceProvider';
import { OnboardingModal } from '@/layout/menuActions';
import { useLocalization } from '@/services/localizationService';
import { TaskType } from '@/services/uploaderService';
import { UserAction, useUser } from '@/services/userService';
import { isLoggedInSelector } from '@/stores/authStore';
import {
  downloadMediaState,
  mediaShownInComparerState,
} from '@/stores/mediaStore';
import { pushModalSelector } from '@/stores/modalStore';
import { tutorialState } from '@/stores/tutorialStore';
import useDownload from '@/features/downloadMedia/downloadSidebar/useDownload';
import { useBulkUploadTutorial } from '@/features/tutorial/bulkUpload/useBulkUploadTutorial';
import {
  trackCompletedEvent,
  trackFailedEvent,
  trackStartedEvent,
} from '@/core/monitoring/trackEvents';
import downloadIcon from '../../public/images/icons/download.svg';

function DownloadButton({
  className = '',
  mediaType,
  isAIToolbarCTAActive,
  ...rest
}: {
  className?: string;
  mediaType: TaskType;
  isAIToolbarCTAActive: boolean;
}) {
  const [isLoading, setIsLoading] = useState(false);

  const isMobile = useMemo(() => deviceType() === DeviceIds.Mobile, []);
  const isLoggedIn = useRecoilValue(isLoggedInSelector);
  const { isPremiumUser } = usePaymentService();

  const pushModal = useSetRecoilState(pushModalSelector);
  const { data: user } = useUser();

  const buttonRef = useRef<HTMLButtonElement>(null);
  const shouldEnableDownload = !!user && isPremiumUser(user);
  const { t, data } = useLocalization();
  const { track } = useTracker();
  const mediasToDownload = useRecoilValue(downloadMediaState);
  const mediaInComparer = useRecoilValue(mediaShownInComparerState);

  const { getMediaType } = useUploaderService();
  const router = useRouter();
  const tutorial = useRecoilValue(tutorialState);

  const { needsExportBeforeDownload, onExportMedia, download } = useDownload();

  const openDownloadSidebar = useCallback(() => {
    const { taskId } = router.query;

    track('download_sidebar', 'opened', {
      mediaType: TaskType.Image,
      taskId,
    });

    pushModal({
      id: 'user:download',
      Modal: DownloadSidebar,
      initialModalScaleSize: 1,
    });
  }, [pushModal, router.query, track]);
  const openDownloadSidebarReference =
    useCallbackReference(openDownloadSidebar);

  const openUrlInNewTab = useCallback(async () => {
    let finalOutputUrl = mediaInComparer.outputUrl;
    if (needsExportBeforeDownload && mediaInComparer.hasWatermark) {
      setIsLoading(true);
      finalOutputUrl = await onExportMedia(mediaInComparer.id);
      setIsLoading(false);
    }
    if (shouldEnableDownload || !mediaInComparer.hasWatermark) {
      // TECNICAL LIMITATION.
      // we can track only the fact that window opens or fails to open.
      // when open a new window we cannot rely on the content loaded in it.
      // it is effectivelly a new window with navigation features and arbitrary content.
      trackStartedEvent(['download', 'window', 'open']);
      const win = window.open(finalOutputUrl);
      if (win) {
        trackCompletedEvent(['download', 'window', 'open']);
      } else {
        trackFailedEvent(['download', 'window', 'open']);
      }
    }
  }, [
    mediaInComparer.hasWatermark,
    mediaInComparer.id,
    mediaInComparer.outputUrl,
    needsExportBeforeDownload,
    onExportMedia,
    shouldEnableDownload,
  ]);
  const openUrlInNewTabReference = useCallbackReference(openUrlInNewTab);

  const onClick = useCallback(async () => {
    const { taskId } = router.query;
    const aiPipeline = mediaInComparer.aiPipeline || {};

    // Downloading the video directly, so we don't show the sidebar.
    // Before this edit, it was handled in the same way mobile was (that is by opening the media in a new tab),
    // but then it looked quite uncomfortable to download a video from a Mobile browser.
    // For this reason, we just moved to this solution which downloads it by default, either being on Mobile or not
    if (mediaType === TaskType.Video) {
      let fileName;
      try {
        fileName = enhancedFileNameFromUrl(mediaInComparer.outputUrl);
      } catch (e) {
        console.log(e);
      }
      await download({
        downloadUrl: mediaInComparer.outputUrl,
        fileName,
      });
      return;
    }

    // Opening a new tab with the image on mobile and letting the users
    // save by themselves is the best choice for mobile, since we don't want
    // to show them the sidebar.
    if (isMobile) {
      track('download', 'tapped', {
        nextAction: shouldEnableDownload
          ? 'user_download'
          : isLoggedIn
          ? 'paywall'
          : 'login',
        // we know for sure it's an image at this point
        mediaType: TaskType.Image,
        taskId,
        aiPipeline,
      });

      if (!shouldEnableDownload) {
        pushModal({
          id: 'page:compare:before-download',
          Modal: OnboardingModal,
          previousAction: UserAction.Download,
          onClose: openUrlInNewTabReference,
        });
      } else {
        await openUrlInNewTab();
      }

      return;
    }

    if (shouldEnableDownload && isAIToolbarCTAActive) {
      pushModal({
        id: 'ai-model-mismatch',
        Modal: AIModelMismatchModal,
        openDownloadSidebar: openDownloadSidebarReference,
      });
    } else {
      openDownloadSidebar();
    }
  }, [
    router.query,
    mediaInComparer.aiPipeline,
    isMobile,
    mediaType,
    shouldEnableDownload,
    isAIToolbarCTAActive,
    track,
    isLoggedIn,
    getMediaType,
    pushModal,
    openUrlInNewTabReference,
    openUrlInNewTab,
    openDownloadSidebarReference,
    openDownloadSidebar,
  ]);

  const { bulkUploadTutorialId } = useBulkUploadTutorial();

  return (
    <button
      id={`${bulkUploadTutorialId}-step3`}
      type="button"
      className={`btn btn--large btn--primary py-2 px-5 md:px-6 rounded-full border-0 ${className} ${
        tutorial.currentStep === 3 && tutorial.id === bulkUploadTutorialId
          ? 'z-20'
          : ''
      }`}
      onClick={onClick}
      ref={buttonRef}
      disabled={
        !mediaType || isLoading || user?.monetization?.hasIncompleteSubscription
      }
      {...rest}
    >
      {!mediaType || isLoading ? (
        <div className="px-12">
          <TailSpin color="white" height="30" width="30" aria-label="loading" />
        </div>
      ) : (
        <>
          {/* "!ms-0" is to remove excessive margin (given by btn--large's space-s-3), while "!-me-2" is to easily reduce right margin (as of Figma) */}
          <div className="hidden md:block !ms-0 !-me-2">
            <p className="M16 sm:M18">{t(data.common.download)}</p>
            {mediaInComparer?.hasWatermark && (
              <p className="M11 sm:M12">{t(data.common.removeWatermark)}</p>
            )}
            {mediasToDownload.length > 1 && (
              <p className="M11 sm:M12">
                {t(data.result.nPhotos, { n: mediasToDownload.length })}
              </p>
            )}
          </div>
          <p className="md:hidden">{t(data.common.download)}</p>
          <img {...downloadIcon} alt="" />
        </>
      )}
    </button>
  );
}

export default DownloadButton;

function enhancedFileNameFromUrl(url: string): string {
  const originalFileName = new URL(url).pathname.split('/').pop();
  const fileExt = originalFileName!.split('.').pop();
  return originalFileName!.replace(fileExt!, `remini-enhanced.${fileExt}`);
}
